import { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { MDSText, MDSTextColor, MDSTextVariant, MDSToggle } from "@/mds";
import { electronModule } from "@/modules/electron";
import {
  SettingsIntegrationHeader,
  SettingsIntegrationPageWrapper,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import { useAppStore } from "@/store";

export const SettingsEditorPage = observer(() => {
  const { store } = useAppStore();
  const [spellcheckEnabled, setSpellcheckEnabled] = useState<boolean | null>(null);
  const [isSavingSpellcheck, setIsSavingSpellcheck] = useState(false);

  useEffect(() => {
    if (!store.interface.isDesktop) return;

    void electronModule.getSpellcheckEnabled().then(({ enabled }) => {
      setSpellcheckEnabled(enabled);
    });
  }, [store.interface.isDesktop]);

  const handleSpellcheckChange = useCallback(async () => {
    if (isSavingSpellcheck || spellcheckEnabled === null) return;

    const enabled = !spellcheckEnabled;
    setIsSavingSpellcheck(true);
    try {
      await electronModule.setSpellcheckEnabled({ enabled });
      setSpellcheckEnabled(enabled);
    } finally {
      setIsSavingSpellcheck(false);
    }
  }, [isSavingSpellcheck, spellcheckEnabled]);

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="General" title="Editor" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          {store.interface.isDesktop && (
            <StyledSettingRow>
              <StyledSettingLabel>
                <MDSText variant={MDSTextVariant.TextS}>Spell check</MDSText>
                <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Tertiary}>
                  Underline misspelled words while typing
                </MDSText>
              </StyledSettingLabel>
              <MDSToggle
                aria-label="Spell check"
                checked={spellcheckEnabled ?? true}
                disabled={isSavingSpellcheck || spellcheckEnabled === null}
                onChange={() => {
                  void handleSpellcheckChange();
                }}
              />
            </StyledSettingRow>
          )}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledSettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 700px;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const StyledSettingLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;
